<template>
    <div>
        <textarea
            ref="textareaRef"
            v-model="dataTextarea"
            :id="id"
            :autofocus="configDefault.autofocus"
            :cols="cols"
            :dirname="configDefault.dirname"
            :form="configDefault.form"
            :maxlength="configDefault.maxlength"
            :name="name"
            :placeholder="configDefault.placeholder"
            :readonly="configDefault.readonly"
            :required="configDefault.required"
            :rows="rows"
            :wrap="configDefault.wrap"
            class="form-control"
            :class="{
                [configDefault.classCustom]: configDefault.classCustom,
                'is-invalid': configDefault.error,
                disabled_cursor: disabled
            }"
            :disabled="disabled"
            @blur="handleBlur"
        ></textarea>
        <slot />
    </div>
</template>

<script>
import { onAutoResizeTextarea } from '@/utils/handlers';

export default {
    name: 'TextareaComponent',
    props: {
        id: {
            type: String,
            default: () => 'textarea-form'
        },
        name: {
            type: String,
            default: () => ''
        },
        model: {
            required: true,
            default: () => ''
        },
        rows: {
            default: () => ''
        },
        cols: {
            default: () => ''
        },
        disabled: {
            type: Boolean,
            default: () => false
        },
        isPreview: {
            type: Boolean,
            default: () => false
        },
        config: {
            type: Object,
            default: () => {
                return {
                    autofocus: 'autofocus',
                    dirname: '',
                    form: 'form_id',
                    maxlength: '',
                    placeholder: '',
                    wrap: '',
                    valueSync: '',
                    errorField: '',
                    errorCustomMess: '',
                    classCustom: '',
                    typesValidate: [],
                    error: true,
                    readonly: false,
                    required: false,
                    minHeight: 37,
                    isInputAutoSize: false
                };
            }
        }
    },
    data() {
        return {
            dataTextarea: '',
            configDefault: this.config,
            capturedMinHeight: this.config.minHeight || 37
        };
    },
    computed: {
        computedIsInputAutoSize() {
            return this.configDefault.isInputAutoSize;
        }
    },
    mounted() {
        if (this.computedIsInputAutoSize) {
            this.initResize();
            window.addEventListener('resize-textarea', this.handleGlobalResize);
            this.initResizeObserver();
        }
    },
    beforeDestroy() {
        if (this.computedIsInputAutoSize) {
            window.removeEventListener('resize-textarea', this.handleGlobalResize);
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
            }
        }
    },
    methods: {
        handleBlur() {
            this.$emit('blur');
        },
        handleGlobalResize(event) {
            if (event && event.detail && event.detail.id && event.detail.id !== this.id) {
                return;
            }
            if (this.$el && this.$el.offsetParent !== null) {
                this.onResize();
            }
        },
        initResize(id = null) {
            if (id && this.id !== id) return;
            this.$nextTick(() => {
                if (this.$refs.textareaRef) {
                    setTimeout(() => {
                        if (this.$refs.textareaRef && !this.isPreview) {
                            this.capturedMinHeight = Math.max(this.capturedMinHeight, this.$refs.textareaRef.offsetHeight);
                        }
                        this.onResize();
                    }, 50);
                }
            });
        },
        onResize(id = null) {
            if (id && this.id !== id) return;
            const execute = () => {
                if (this.$refs.textareaRef) {
                    const minH = this.capturedMinHeight;
                    onAutoResizeTextarea(this.$refs.textareaRef, minH);
                }
            };
            this.$nextTick(execute);
            setTimeout(execute, 100);
            setTimeout(execute, 500);
        },
        initResizeObserver() {
            if (this.$refs.textareaRef && window.ResizeObserver) {
                this.resizeObserver = new ResizeObserver(() => {
                    const el = this.$refs.textareaRef;
                    if (el) {
                        const isOverflowing = el.scrollHeight > el.clientHeight + 2;
                        el.style.setProperty('overflow-y', isOverflowing ? 'auto' : 'hidden', 'important');
                        el.style.setProperty('overflow-x', 'hidden', 'important');
                    }
                });
                this.resizeObserver.observe(this.$refs.textareaRef);
            }
        }
    },
    watch: {
        model: {
            handler: function (newModel) {
                this.dataTextarea = newModel;
            },
            immediate: true
        },

        config: {
            handler: function (newConfig) {
                this.configDefault = {
                    ...this.configDefault,
                    ...newConfig
                };
            },
            immediate: true,
            deep: true
        },

        dataTextarea: {
            handler: function () {
                this.configDefault.errorCustomMess = '';
                this.configDefault.error = false;
                this.configDefault.valueSync = this.dataTextarea;

                this.$emit('update:model', this.dataTextarea);
                this.$emit('update:config', this.configDefault);
                if (this.computedIsInputAutoSize) {
                    this.onResize();
                }
            },
            immediate: true,
            deep: true
        },
        isPreview: {
            handler: function () {
                if (this.computedIsInputAutoSize) {
                    this.onResize();
                }
            }
        }
    }
};
</script>

<style lang="scss" scoped></style>
